import { useCallback, useEffect, useState } from "react";
import { CalSelectedCalendar, CalendarIntegrationType } from "@/types/calendar";
import { useCalApi } from "./useCalApi";

type SelectedCalendarsResponse = {
  selectedCalendars: CalSelectedCalendar[];
};

type ToggleCalendarParams = {
  integration: CalendarIntegrationType;
  externalId: string;
  credentialId: number;
};

type UseSelectedCalendarsResult = {
  isLoading: boolean;
  selectedCalendars: CalSelectedCalendar[];
  refetch: () => Promise<void>;
  addCalendar: (params: ToggleCalendarParams) => Promise<boolean>;
  removeCalendar: (params: ToggleCalendarParams) => Promise<boolean>;
  isToggling: boolean;
};

/**
 * Hook to manage selected calendars for conflict checking.
 * Replaces @calcom/atoms SelectedCalendarsSettings component logic.
 */
export function useSelectedCalendars(): UseSelectedCalendarsResult {
  const { fetchCalApi } = useCalApi();
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [selectedCalendars, setSelectedCalendars] = useState<CalSelectedCalendar[]>([]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchCalApi<SelectedCalendarsResponse>("/selected-calendars");
      setSelectedCalendars(result?.selectedCalendars || []);
    } finally {
      setIsLoading(false);
    }
  }, [fetchCalApi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addCalendar = useCallback(
    async (params: ToggleCalendarParams): Promise<boolean> => {
      setIsToggling(true);
      try {
        const result = await fetchCalApi<{ success: boolean }>("/selected-calendars", {
          method: "POST",
          body: params,
        });

        if (result?.success) {
          // Optimistically update local state
          setSelectedCalendars((prev) => [
            ...prev,
            {
              id: params.externalId,
              externalId: params.externalId,
              integration: params.integration,
              credentialId: params.credentialId,
            },
          ]);
          return true;
        }
        return false;
      } finally {
        setIsToggling(false);
      }
    },
    [fetchCalApi]
  );

  const removeCalendar = useCallback(
    async (params: ToggleCalendarParams): Promise<boolean> => {
      setIsToggling(true);
      try {
        const result = await fetchCalApi<{ success: boolean }>("/selected-calendars", {
          method: "DELETE",
          body: params,
        });

        if (result?.success) {
          // Optimistically update local state
          setSelectedCalendars((prev) =>
            prev.filter((c) => c.externalId !== params.externalId)
          );
          return true;
        }
        return false;
      } finally {
        setIsToggling(false);
      }
    },
    [fetchCalApi]
  );

  return {
    isLoading,
    selectedCalendars,
    refetch: fetchData,
    addCalendar,
    removeCalendar,
    isToggling,
  };
}
